import { DOMUtil, VerticalAlignment } from "@mcleod/core";
import { AbstractCustomTableToolsPanel } from "./customTools/AbstractCustomTableToolsPanel";
import { AbstractStandardTableToolsPanel } from "./standardTools/AbstractStandardTableToolsPanel";
import { Component, DesignerInterface, Textbox } from "../../..";
import { ComponentCreator } from "../../../page/ComponentFactory";
import { CustomCenterTableToolsPanel } from "./customTools/CustomCenterTableToolsPanel";
import { CustomLeftTableToolsPanel } from "./customTools/CustomLeftTableToolsPanel";
import { CustomRightTableToolsPanel } from "./customTools/CustomRightTableToolsPanel";
import { DesignableObjectLogManager } from "../../../logging/DesignableObjectLogManager";
import { EllipsisActionsPanel } from "./standardTools/EllipsisActionsPanel";
import { HorizontalSpacer } from "../../../page/HorizontalSpacer";
import { Panel } from "../../panel/Panel";
import { PanelProps } from "../../panel/PanelProps";
import { RowCountLabel } from "./searchFilter/RowCountLabel";
import { SearchFilterPanel } from "./searchFilter/SearchFilterPanel";
import { StandardLeftToolsPanel } from "./standardTools/StandardLeftToolsPanel";
import { StandardRightToolsPanel } from "./standardTools/StandardRightToolsPanel";
import { Table } from "../Table";

enum InnerIds {
    HS_TOOL_SPACER = "hsToolSpacer",
    PANEL_CUSTOM_CENTER_TOOLS = "panelCustomCenterTools",
    PANEL_CUSTOM_LEFT_TOOLS = "panelCustomLeftTools",
    PANEL_CUSTOM_RIGHT_TOOLS = "panelCustomRightTools",
    PANEL_ELLIPSIS_ACTIONS = "panelEllipsisActions",
    PANEL_SEARCH_FILTER = "panelSearchFilter",
    PANEL_STANDARD_LEFT_TOOLS = "panelStandardLeftTools",
    PANEL_STANDARD_RIGHT_TOOLS = "panelStandardRightTools"
}

const log = DesignableObjectLogManager.getLogger("components.table.TableHeadingPanel")

export class TableHeadingPanel extends Panel {
    protected panelCustomCenterTools: CustomCenterTableToolsPanel;
    protected panelCustomLeftTools: CustomLeftTableToolsPanel;
    protected panelCustomRightTools: CustomRightTableToolsPanel;
    protected panelEllipsisActions: EllipsisActionsPanel;
    private panelSearchFilter: SearchFilterPanel;
    private panelSearchFilterSpacer: Panel;
    protected panelStandardLeftTools: StandardLeftToolsPanel;
    protected panelStandardRightTools: StandardRightToolsPanel;
    protected readonly table: Table;
    protected hsToolSpacer: HorizontalSpacer;

    constructor(table: Table, props?: Partial<PanelProps>) {
        super({
            verticalAlign: VerticalAlignment.CENTER,
            _designer: table._designer,
            wrap: false,
            ...props
        });
        this.allowDropInDesigner = false;
        this.table = table;

        this.panelSearchFilter = new SearchFilterPanel(this.table);

        this.panelStandardLeftTools = new StandardLeftToolsPanel(this.table);
        this.panelEllipsisActions = new EllipsisActionsPanel(this.table);
        this.panelStandardRightTools = new StandardRightToolsPanel(this.table);

        this.panelCustomCenterTools = new CustomCenterTableToolsPanel(this.table);
        this.panelCustomLeftTools = new CustomLeftTableToolsPanel(this.table);
        this.panelCustomRightTools = new CustomRightTableToolsPanel(this.table);

        this.hsToolSpacer = new HorizontalSpacer();

        this.add(
            this.panelCustomLeftTools,
            this.panelSearchFilter,
            this.panelStandardLeftTools,
            this.panelCustomCenterTools,
            this.panelEllipsisActions,
            this.hsToolSpacer,
            this.panelCustomRightTools,
            this.panelStandardRightTools
        );

        this.parent = this.table as any; // table isn't a Container, so the "as any" is necessary
        this._designer = table._designer;
        this.addFilterPanelPlacementResizeListener();
    }

    private addFilterPanelPlacementResizeListener() {
        this.addResizeListener(() => this.panelSearchFilter.placeFilterPanel());
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.hsToolSpacer, InnerIds.HS_TOOL_SPACER);
        this.setInnerComponentId(this.panelCustomCenterTools, InnerIds.PANEL_CUSTOM_CENTER_TOOLS);
        this.setInnerComponentId(this.panelCustomLeftTools, InnerIds.PANEL_CUSTOM_LEFT_TOOLS);
        this.setInnerComponentId(this.panelCustomRightTools, InnerIds.PANEL_CUSTOM_RIGHT_TOOLS);
        this.setInnerComponentId(this.panelEllipsisActions, InnerIds.PANEL_ELLIPSIS_ACTIONS);
        this.setInnerComponentId(this.panelSearchFilter, InnerIds.PANEL_SEARCH_FILTER);
        this.setInnerComponentId(this.panelStandardLeftTools, InnerIds.PANEL_STANDARD_LEFT_TOOLS);
        this.setInnerComponentId(this.panelStandardRightTools, InnerIds.PANEL_STANDARD_RIGHT_TOOLS);
    }

    get textboxSearch(): Textbox {
        return this.panelSearchFilter.panelSearch.get();
    }

    get textboxFilter(): Textbox {
        return this.panelSearchFilter.panelFilter.get();
    }

    get labelRowCount(): RowCountLabel {
        return this.panelSearchFilter.panelRowCount.labelRowCount;
    }

    refreshTextboxSearchTooltip() {
        this.panelSearchFilter.refreshTextboxSearchTooltip();
    }

    refreshRowCountPanel() {
        this.panelSearchFilter.refreshRowCountPanel();
    }

    syncSearchFilterFields() {
        this.panelSearchFilter.syncFields(true);
    }

    doAfterDbSearch() {
        this.panelSearchFilter.doAfterDbSearch();
    }

    focusSearchFilter() {
        this.panelSearchFilter.focusSearchFilter();
    }

    syncStandardTools() {
        if (this.table.isDeserializing() === true) {
            log.debug("Not syncing table tools, table is still being deserialized");
            return;
        }
        this.panelStandardLeftTools.sync();
        this.panelEllipsisActions.sync();
        this.panelStandardRightTools.sync();
    }

    public set addButtonCaption(value: string) {
        this.panelStandardRightTools.buttonAdd.caption = value;
    }

    syncDefaultDetailButton() {
        this.panelStandardLeftTools.syncDefaultDetailButton();
    }

    private get standardTableToolsPanels(): AbstractStandardTableToolsPanel[] {
        return [ this.panelStandardLeftTools, this.panelEllipsisActions, this.panelStandardRightTools ];
    }

    get customTableToolsPanels(): AbstractCustomTableToolsPanel[] {
        return [ this.panelCustomLeftTools, this.panelCustomCenterTools, this.panelCustomRightTools ];
    }

    checkToolAvailability() {
        this.standardTableToolsPanels.forEach(standardToolsPanel => standardToolsPanel.checkToolAvailability());
        this.customTableToolsPanels.forEach(customToolsPanel => customToolsPanel.checkToolAvailability());
    }

    /**
     * Don't call this method directly; call Table.addLeftTool() instead.
     */
    addLeftTool(tool: ComponentCreator): Component {
        return this.panelCustomLeftTools.addTool(tool);
    }

    /**
     * Don't call this method directly; call Table.addCenterTool() instead.
     */
    addCenterTool(tool: ComponentCreator): Component {
        return this.panelCustomCenterTools.addTool(tool);
    }

    /**
     * Don't call this method directly; call Table.addRightTool() instead.
     */
    addRightTool(tool: ComponentCreator): Component {
        return this.panelCustomRightTools.addTool(tool);
    }

    removeTool(...tools: Component[]) {
        const customToolsPanels = this.customTableToolsPanels;
        tools?.forEach(tool => customToolsPanels.forEach(toolsPanel => toolsPanel.remove(tool)));
    }

    serializeCustomTools(toolsPanelSerializer: (components: Component[], name: string) => string): string {
        let result = "";
        result += toolsPanelSerializer(this.panelCustomLeftTools.components, "leftTools");
        result += toolsPanelSerializer(this.panelCustomCenterTools.components, "centerTools");
        result += toolsPanelSerializer(this.panelCustomRightTools.components, "rightTools");
        return result;
    }

    protected gatherTools(condition: (component: Component) => boolean): Component[] {
        return [
            ...this.getToolsFromPanel(this.panelCustomLeftTools, condition),
            ...this.getToolsFromPanel(this.panelStandardLeftTools, condition),
            ...this.getToolsFromPanel(this.panelCustomCenterTools, condition),
            ...this.getToolsFromPanel(this.panelEllipsisActions, condition),
            ...this.getToolsFromPanel(this.panelCustomRightTools, condition),
            ...this.getToolsFromPanel(this.panelStandardRightTools, condition)
        ];
    }

    private getToolsFromPanel(panel: Panel, condition: (component: Component) => boolean): Component[] {
        return panel?.components?.filter(component => condition(component));
    }

    override get _designer(): DesignerInterface {
        return super._designer;
    }

    override set _designer(value: DesignerInterface) {
        super._designer = value;
        this.components.forEach(component => component._designer = value);
    }

    displayFilterBelowSearch(value: boolean) {
        if (value === true) {
            // If the filter panel is set to appear below the search panel:
            // - Create the spacer panel, and set its width equal to the width of the custom left tools panel.
            // - Attach a resize listener to the custom left tools panel so that the spacer panel's width can mirror the
            //   width of the custom left tools panel.
            // - Add the spacer panel to the heading panel (on the second row).
            // - Add the filter panel to the heading panel (after the spacer panel).  It will have already been removed
            //   from the SearchFilterPanel by this point.
            this.createPanelSearchFilterSpacer();
            this.panelCustomLeftTools.addResizeListener(this.setPanelSearchFilterSpacerWidth);
            this.addIfNotPresent(this.panelSearchFilterSpacer);
            this.addIfNotPresent(this.panelSearchFilter.panelFilter);
        } else {
            // The filter panel is not set to appear below the search panel, so:
            // - Remove the resize listener from the custom left tools panel.
            // - Remove the spacer panel from the heading panel, and destroy it.
            // - Remove the filter panel from the heading panel.
            // - The filter panel will be re-added to the SearchFilterPanel after this method completes
            //   (in SearchFilterPanel).
            this.panelCustomLeftTools.removeResizeListener(this.setPanelSearchFilterSpacerWidth);
            this.remove(this.panelSearchFilterSpacer);
            this.remove(this.panelSearchFilter.panelFilter);
            this.panelSearchFilterSpacer = null;
        }
    }

    private createPanelSearchFilterSpacer() {
        if (this.panelSearchFilterSpacer == null) {
            this.panelSearchFilterSpacer = Panel.createNoMarginNoPaddingPanel({
                id: this.createInnerComponentId("panelSearchFilterSpacer"),
                rowBreak: false
            });
        }
        this.setPanelSearchFilterSpacerWidth();
    }

    private setPanelSearchFilterSpacerWidth = () => {
        if (this.panelSearchFilterSpacer != null) {
            this.panelSearchFilterSpacer.width =
                DOMUtil.getElementWidth(this.panelCustomLeftTools.element, { includeMargin: true});
        }
    };

    getInnerPanelsMaxHeight(exclusions: Component[]): number {
        let result = 0;
        this.components
            .filter(component => !exclusions.includes(component))
            .forEach(component => {
                result = Math.max(result, DOMUtil.getElementHeight(component._element));
            }
        );
        return result;
    }
}
